import React from 'react';
import { motion } from 'framer-motion';
import ContentPage from '../ContentPage';

const About: React.FC = () => {
  return (
    <ContentPage title="About SokoVibe" accent="purple">
      {/* Subtle tree background */}
      <div className="absolute right-0 top-10 opacity-5 pointer-events-none">
        <img src="/assets/tree.svg" alt="" className="w-64 h-64" />
      </div>
      
      {/* Introduction section */}
      <div className="relative mb-12">
        <h2 className="text-2xl font-bold mb-4 text-gray-800">
          Welcome to SokoVibe
        </h2>
        <p className="mb-6 text-lg leading-relaxed text-gray-700">
          SokoVibe is a premier online marketplace dedicated to showcasing and celebrating authentic Afro-Caribbean products and businesses in the diaspora.
        </p>
        <p className="mb-6 text-gray-600">
          Founded with a passion for cultural preservation and economic empowerment, SokoVibe serves as a bridge connecting talented creators, artisans, and entrepreneurs with conscious consumers seeking authentic, quality products with cultural significance.
        </p>
      </div>
      
      {/* Mission and Vision section - clean, elegant text */}
      <div className="mb-12">
        <div className="mb-8">
          <h3 className="text-xl font-semibold mb-3 text-gray-700 flex items-center">
            <span className="text-2xl mr-2 text-gray-400">✦</span> Our Mission
          </h3>
          <p className="pl-8 border-l-2 border-gray-200 py-2 text-gray-600">
            To create a vibrant digital space where the rich cultural heritage of the Afro-Caribbean community can thrive and reach global audiences who appreciate its unique value and significance.
          </p>
        </div>
        
        <div className="mb-8">
          <h3 className="text-xl font-semibold mb-3 text-gray-700 flex items-center">
            <span className="text-2xl mr-2 text-gray-400">★</span> Our Vision
          </h3>
          <p className="pl-8 border-l-2 border-gray-200 py-2 text-gray-600">
            To become the leading global marketplace for Afro-Caribbean products, empowering businesses and preserving cultural heritage through commerce and community.
          </p>
        </div>
      </div>
      
      {/* Quote - simple, elegant */}
      <div className="mb-12">
        <blockquote className="relative pl-6 py-4 italic">
          <div className="absolute left-0 top-0 h-full w-1 bg-gray-300"></div>
          <p className="text-lg text-gray-600">
            "We believe in the power of cultural commerce to transform communities and preserve heritage."
          </p>
          <p className="mt-2 text-sm text-right text-gray-500 font-medium">— SokoVibe Founding Team</p>
        </blockquote>
      </div>
      
      {/* Values - simplified, elegant */}
      <div className="mb-12">
        <h3 className="text-xl font-semibold mb-6 text-gray-700">Our Values</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-y-6 gap-x-12">
          {[
            {
              title: "Authenticity",
              description: "We celebrate and promote genuine cultural expression",
              icon: "✦"
            },
            {
              title: "Community",
              description: "We foster connections between creators and consumers",
              icon: "♢"
            },
            {
              title: "Quality",
              description: "We uphold high standards for all products on our platform",
              icon: "★"
            },
            {
              title: "Empowerment",
              description: "We provide tools and opportunities for business growth",
              icon: "◈"
            }
          ].map((value, index) => (
            <motion.div 
              key={index}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 * index }}
              className="flex items-start"
            >
              <span className="text-2xl mr-3 text-gray-400">{value.icon}</span>
              <div>
                <h4 className="font-semibold text-gray-700">{value.title}</h4>
                <p className="text-gray-600">{value.description}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    
    </ContentPage>
  );
};

export default About;
